import { Popover, PopoverContent } from '@carbon/react';
import { useEffect, useId, useRef, useState, type KeyboardEvent } from 'react';
import { CdAvatar } from '../../atoms/CdAvatar';
import { cx } from '../../utils';
import type { CdActionMenuItem } from '../CdActionMenu';
import styles from './CdAccountMenu.module.scss';

export interface CdAccountMenuProps {
  /** Accessible name for the trigger, e.g. "Account". */
  label: string;
  /** Full name shown at the top of the menu. */
  name?: string;
  email?: string;
  /** Uppercase initials for the avatar; the user icon is shown until they are known. */
  initials?: string;
  /** Shows the red badge dot on the trigger avatar. */
  hasBadge?: boolean;
  items: CdActionMenuItem[];
  className?: string;
}

/**
 * Account avatar that opens a profile card: avatar, name and email first, then the actions.
 * Built on Carbon Popover because OverflowMenu cannot render anything above its items.
 */
export const CdAccountMenu = ({
  label,
  name,
  email,
  initials,
  hasBadge = false,
  items,
  className,
}: CdAccountMenuProps) => {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const menuId = useId();
  const hasProfile = [name, email].some(Boolean);

  const menuItems = () => [...(listRef.current?.querySelectorAll('button') ?? [])];

  useEffect(() => {
    if (open) menuItems()[0]?.focus();
  }, [open]);

  const close = (restoreFocus: boolean) => {
    setOpen(false);
    if (restoreFocus) triggerRef.current?.focus();
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (!open) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      close(true);
      return;
    }
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
    event.preventDefault();
    const buttons = menuItems();
    const current = buttons.indexOf(document.activeElement as HTMLButtonElement);
    const step = event.key === 'ArrowDown' ? 1 : -1;
    buttons[(current + step + buttons.length) % buttons.length]?.focus();
  };

  return (
    // Keyboard handling for the open menu (Esc, arrows) lives on the wrapper so it covers
    // both the trigger and the items.
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    <div className={cx(styles.root, className)} onKeyDown={handleKeyDown}>
      <Popover
        open={open}
        align="bottom-end"
        caret={false}
        dropShadow
        onRequestClose={() => close(false)}
      >
        <button
          ref={triggerRef}
          type="button"
          className={styles.trigger}
          aria-label={label}
          aria-haspopup="menu"
          aria-expanded={open}
          aria-controls={open ? menuId : undefined}
          onClick={() => setOpen((value) => !value)}
        >
          <CdAvatar size="lg" initials={initials} hasBadge={hasBadge} />
        </button>
        <PopoverContent className={styles.card}>
          {hasProfile && (
            <div className={styles.profile}>
              <CdAvatar size="xl" initials={initials} className={styles.profileAvatar} />
              <div className={styles.identity}>
                {name && (
                  <p className={styles.name} title={name}>
                    {name}
                  </p>
                )}
                {email && (
                  <p className={styles.email} title={email}>
                    {email}
                  </p>
                )}
              </div>
            </div>
          )}
          <ul ref={listRef} id={menuId} role="menu" aria-label={label} className={styles.list}>
            {items.map((item) => (
              <li key={item.id} role="none" className={cx(item.divider && styles.divider)}>
                <button
                  type="button"
                  role="menuitem"
                  className={cx(styles.item, item.danger && styles.danger)}
                  onClick={() => {
                    close(true);
                    item.onSelect?.();
                  }}
                >
                  {item.icon && <item.icon aria-hidden="true" />}
                  {item.label}
                </button>
              </li>
            ))}
          </ul>
        </PopoverContent>
      </Popover>
    </div>
  );
};
